import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Input } from "../../../components/Input";
import Select, { Option } from "../../../components/Select";
import { Slider } from "../../../components/Slider";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { queryClient } from "../../../queryClient";
import editIcon from "../../../assets/settings/edit.svg";
import { Image } from "../../../components/Image";
import { useGetProfileRelationTypes } from "../../../data/useGetProfileRelationTypes";
import { useCreateProfileRelationType } from "../../../data/useCreateProfileRelationType";
import { useEditProfileRelationType } from "../../../data/useEditProfileRelationType";
import { useUpdateProfileRelationTypeOrder } from "../../../data/useUpdateProfileRelationTypeOrder";
import { useDeleteProfileRelationType } from "../../../data/useDeleteProfileRelationType";
import { BiGridVertical } from "react-icons/bi";
import { Label } from "../../../components/Label";
import { Modal } from "../../../components/Modal";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { Form, Formik } from "formik";
import { profileRelationshipTypeValidationSchema } from "../../../utils/validationSchema";

type RiskLower = "low" | "medium" | "high";
const riskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
] as const;

type RowType = {
  id: number;
  name: string;
  entity_type: { id: number; name: string };
  percentage_required: boolean;
  description_required: boolean;
  risk_level?: RiskLower | string | null;
};

type RelationshipFormValues = {
  name: string;
  entity_type: number | null;
  percentage_required: boolean;
  description_required: boolean;
  risk_level: RiskLower | null;
};

type DragState = { draggingId: number | null; overId: number | null };

const COLS = "minmax(280px,1fr) 240px 150px 130px 130px 80px";
const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "14px 16px",
  borderTop: "1px solid #F3F4F6",
};

const RiskPill = ({ value }: { value?: string | null }) => {
  const v = value?.toLowerCase() as RiskLower;
  const label =
    v === "low"
      ? "Low"
      : v === "high"
      ? "High"
      : v === "medium"
      ? "Medium"
      : "-";
  const riskLevel = v === "low" ? "low" : v === "high" ? "high" : "medium";
  const bg = theme.colors.risk[riskLevel].bg;
  const color = theme.colors.risk[riskLevel].color;
  return (
    <Paragraph
      style={{
        display: "inline-block",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        background: bg,
        color,
        minWidth: "auto",
        textAlign: "center",
      }}
    >
      {label}
    </Paragraph>
  );
};

const Row = ({
  item,
  onEdit,
  draggableProps,
  isDragging,
}: {
  item: RowType;
  onEdit: (item: RowType) => void;
  draggableProps: any;
  isDragging: boolean;
}) => (
  <div
    style={{
      ...bodyRowStyle,
      background: isDragging ? "#F9FAFB" : "transparent",
      cursor: "grab",
    }}
    {...draggableProps}
  >
    <Flex alignItems="center" gap="12">
      <BiGridVertical size={18} style={{ opacity: 0.7 }} />
      <Paragraph fontSize="2" fontWeight="600">
        {item.name}
      </Paragraph>
    </Flex>

    <Paragraph color="gray.80">{item.entity_type?.name ?? "-"}</Paragraph>

    <RiskPill value={item.risk_level || "-"} />

    <Paragraph color="gray.80">{item.percentage_required ? "Yes" : "No"}</Paragraph>

    <Paragraph color="gray.80">{item.description_required ? "Yes" : "No"}</Paragraph>

    <Flex>
      <Image
        src={editIcon}
        alt="edit icon"
        height="24px"
        width="24px"
        style={{ cursor: "pointer" }}
        onClick={() => onEdit(item)}
      />
    </Flex>
  </div>
);

const BoolRadio: React.FC<{
  value: boolean;
  onChange: (next: boolean) => void;
  name: string;
  disabled?: boolean;
}> = ({ value, onChange, name, disabled }) => (
  <Flex alignItems="center" gap="16" role="radiogroup">
    <Label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <input
        type="radio"
        name={name}
        value="true"
        checked={value === true}
        onChange={() => onChange(true)}
        disabled={disabled}
      />
      Yes
    </Label>
    <Label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <input
        type="radio"
        name={name}
        value="false"
        checked={value === false}
        onChange={() => onChange(false)}
        disabled={disabled}
      />
      No
    </Label>
  </Flex>
);

const SettingsPropertiesProfileRelationshipTypes = () => {
  const { data, isLoading, isError } = useGetProfileRelationTypes();
  const { mutate: createRelation } = useCreateProfileRelationType();
  const { mutate: editRelation } = useEditProfileRelationType();
  const { mutate: deleteRelation } = useDeleteProfileRelationType();
  const { mutate: updateOrder } = useUpdateProfileRelationTypeOrder();
  const { data: entityTypes } = useEntityTypes();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [doubleClickOnDelete, setDoubleClickOnDelete] = useState(false);

  const entityTypeOptions: Option[] = (entityTypes?.data ?? []).map(
    ({ id, name }: { id: number; name: string }) => ({
      label: name,
      value: id,
    })
  );

  const items = useMemo<RowType[]>(
    () => (Array.isArray(data) ? (data as RowType[]) : []),
    [data]
  );

  const [list, setList] = useState<RowType[]>([]);
  useEffect(() => setList(items), [items]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["profile_relationship_type"] });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"add" | "edit">("add");
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formName, setFormName] = useState("");
  const [formEntityTypeId, setFormEntityTypeId] = useState<number | null>(null);
  const [formRisk, setFormRisk] = useState<RiskLower | null>(null);
  const [formPercentage, setFormPercentage] = useState<boolean>(false);
  const [formDescription, setFormDescription] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormName("");
    setFormEntityTypeId(null);
    setFormRisk(null);
    setFormPercentage(false);
    setFormDescription(false);
    setIsFormOpen(true);
  };

  const openEdit = (item: RowType) => {
    setFormMode("edit");
    setEditingId(item.id);
    setFormName(item.name ?? "");
    setFormEntityTypeId(item.entity_type?.id ?? null);
    setFormRisk(
      item.risk_level && item.risk_level !== "-"
        ? (item.risk_level.toLowerCase() as RiskLower)
        : null
    );
    setFormPercentage(Boolean(item.percentage_required));
    setFormDescription(Boolean(item.description_required));
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
  };

  const onSuccess = (msg: string) => {
    notify(msg, { type: "success", position: "bottom-left" });
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
    setIsFormOpen(false);
    invalidate();
  };

  const onError = (err: any, msg: string) => {
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    showError(err, msg);
    setDoubleClickOnDelete(false);
  };

  const handleCreate = (values: RelationshipFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    createRelation(
      {
        name: values.name.trim(),
        entity_type: values.entity_type,
        risk_level: values.risk_level || null,
        percentage_required: values.percentage_required,
        description_required: values.description_required,
      } as any,
      {
        onSuccess: () => onSuccess("Relationship type added successfully!"),
        onError: (err) =>
          onError(
            err,
            "A problem occurred while creating the relationship type."
          ),
      }
    );
  };

  const handleUpdate = (values: RelationshipFormValues) => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);

    editRelation(
      {
        id: editingId,
        params: {
          name: values.name.trim(),
          entity_type: values.entity_type,
          risk_level: values.risk_level || null,
          percentage_required: values.percentage_required,
          description_required: values.description_required,
        },
      } as any,
      {
        onSuccess: () => onSuccess("Relationship type edited successfully!"),
        onError: (err) =>
          onError(
            err,
            "A problem occurred while updating the relationship type."
          ),
      }
    );
  };

  const handleDelete = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);
    setDoubleClickOnDelete(true);
    deleteRelation({ id: editingId } as any, {
      onSuccess: () => onSuccess("Relationship type deleted successfully!"),
      onError: (err) =>
        onError(
          err,
          "A problem occurred while deleting the relationship type."
        ),
    });
  };

  const dragState = useRef<DragState>({ draggingId: null, overId: null });
  const reorder = (arr: RowType[], startId: number, endId: number) => {
    const a = arr.findIndex((x) => x.id === startId);
    const b = arr.findIndex((x) => x.id === endId);
    if (a < 0 || b < 0 || a === b) return arr;
    const next = [...arr];
    const [moved] = next.splice(a, 1);
    next.splice(b, 0, moved);
    return next;
  };

  const handleDragStart = (id: number) => (e: React.DragEvent) => {
    dragState.current = { draggingId: id, overId: id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };
  const handleDragOver = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDragEnter = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const { draggingId, overId } = dragState.current;
    if (!draggingId || !overId || draggingId === overId) return;

    const reordered = reorder(list, draggingId, overId);
    setList(reordered);

    updateOrder(
      {
        orders: reordered.map((row, idx) => ({ id: row.id, order: idx })),
      },
      {
        onSuccess: () => {
          notify("Order updated!", {
            type: "success",
            position: "bottom-left",
          });
          invalidate();
        },
        onError: (err) => {
          showError(err, "Failed to update order.");
          invalidate();
        },
      }
    );

    dragState.current = { draggingId: null, overId: null };
  };
  const handleDragEnd = () =>
    (dragState.current = { draggingId: null, overId: null });

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <H2 fontSize="3">Profile relationship types</H2>
          <Paragraph color="gray.60"></Paragraph>
        </Box>
        <Button Icon={FaPlus} iconSize="16px" onClick={openAdd}>
          Add
        </Button>
      </Flex>

      <div style={headStyle}>
        <Paragraph>Name</Paragraph>
        <Paragraph>Entity type</Paragraph>
        <Paragraph>Risk level</Paragraph>
        <Paragraph>Percentage</Paragraph>
        <Paragraph>Description</Paragraph>
        <Paragraph></Paragraph>
      </div>

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load relationship types.
        </Paragraph>
      ) : list.length === 0 ? (
        <Paragraph color="gray.60" px={4} py={4}>
          No relationship types found.
        </Paragraph>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{ borderTop: "1px solid #F3F4F6" }}
        >
          {list.map((item) => (
            <Row
              key={item.id}
              item={item}
              onEdit={openEdit}
              isDragging={dragState.current.draggingId === item.id}
              draggableProps={{
                draggable: true,
                onDragStart: handleDragStart(item.id),
                onDragOver: handleDragOver(item.id),
                onDragEnter: handleDragEnter(item.id),
                onDragEnd: handleDragEnd,
              }}
            />
          ))}
        </div>
      )}

      <Slider isOpen={isFormOpen} onRequestClose={closeForm} width="640px">
        <Formik
          initialValues={{
            name: formName,
            entity_type: formEntityTypeId,
            risk_level: formRisk,
            percentage_required: formPercentage,
            description_required: formDescription,
          }}
          enableReinitialize
          validationSchema={profileRelationshipTypeValidationSchema}
          validateOnMount
          onSubmit={(values) => {
            setFormName(values.name);
            setFormEntityTypeId(values.entity_type);
            setFormRisk(values.risk_level);
            setFormPercentage(values.percentage_required);
            setFormDescription(values.description_required);

            if (formMode === "add") {
              handleCreate(values as RelationshipFormValues);
            } else {
              handleUpdate(values as RelationshipFormValues);
            }
          }}
        >
          {(props) => (
            <Form>
              <Box px={4} py={3}>
                <H2 mb={4}>
                  {formMode === "add"
                    ? "Add relationship type"
                    : "Edit relationship type"}
                </H2>

                {/* Name */}
                <Box mb={3}>
                  <Paragraph mb={1} fontWeight="600">
                    Name
                  </Paragraph>
                  <Input
                    name="name"
                    value={props.values.name}
                    onChange={props.handleChange}
                    placeholder="e.g., Director"
                    isDisabled={isSubmitting}
                    isRequired
                    hasError={Boolean(props.errors.name)}
                    errorMessage={props.errors.name as string}
                  />
                </Box>

                <Box mb={3}>
                  <Paragraph mb={1} fontWeight="600">
                    Entity type
                  </Paragraph>

                  <Select
                    name="entity_type"
                    value={entityTypeOptions.find(
                      ({ value }) => value === props.values.entity_type
                    )}
                    options={entityTypeOptions}
                    onChange={(option: Option) =>
                      props.setFieldValue(
                        "entity_type",
                        (option?.value as number) ?? null
                      )
                    }
                    width="100%"
                    isDisabled={isSubmitting}
                    hasError={Boolean(props.errors.entity_type)}
                    errorMessage={props.errors.entity_type as string}
                  />

                  {entityTypeOptions.length === 0 ? (
                    <Paragraph color="gray.60" fontSize="1" mt={1}>
                      No entity types found. You can still type an ID below.
                    </Paragraph>
                  ) : null}
                </Box>

                {entityTypeOptions.length === 0 ? (
                  <Box mb={3}>
                    <Paragraph mb={1} fontWeight="600">
                      Entity type ID (manual)
                    </Paragraph>
                    <Input
                      value={
                        props.values.entity_type !== null
                          ? String(props.values.entity_type)
                          : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        props.setFieldValue(
                          "entity_type",
                          v === "" ? null : Number(v)
                        );
                      }}
                      placeholder="Enter entity type ID"
                      isDisabled={isSubmitting}
                    />
                  </Box>
                ) : null}

                <Box mb={3}>
                  <Paragraph mb={1} fontWeight="600">
                    Risk level
                  </Paragraph>
                  <Select
                    name="risk_level"
                    options={riskOptions as any}
                    value={
                      riskOptions.find(
                        (option) => option.value === props.values.risk_level
                      ) ?? null
                    }
                    onChange={(option: Option) =>
                      props.setFieldValue(
                        "risk_level",
                        (option?.value as RiskLower) ?? null
                      )
                    }
                    width="100%"
                    isDisabled={isSubmitting}
                    hasError={Boolean(props.errors.risk_level)}
                    errorMessage={props.errors.risk_level as string}
                  />
                </Box>

                <Box mb={3}>
                  <Paragraph mb={1} fontWeight="600">
                    Requires percentage?
                  </Paragraph>
                  <BoolRadio
                    name="percentage"
                    value={props.values.percentage_required}
                    onChange={(next) => props.setFieldValue("percentage_required", next)}
                    disabled={isSubmitting}
                  />
                </Box>

                <Box mb={5}>
                  <Paragraph mb={1} fontWeight="600">
                    Requires description?
                  </Paragraph>
                  <BoolRadio
                    name="description"
                    value={props.values.description_required}
                    onChange={(next) =>
                      props.setFieldValue("description_required", next)
                    }
                    disabled={isSubmitting}
                  />
                </Box>

                <Flex justifyContent="space-between" alignItems="center">
                  {formMode === "edit" ? (
                    <Button
                      variant="danger"
                      Icon={FaTrash}
                      iconSize="12px"
                      onClick={() => setIsDeleteModalOpen(true)}
                      isDisabled={isSubmitting}
                      iconColor="white"
                    >
                      Delete
                    </Button>
                  ) : (
                    <span />
                  )}

                  <Flex gap="12" alignItems="center">
                    <Button
                      variant="secondary"
                      onClick={closeForm}
                      isDisabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isDisabled={
                        !props.isValid || !props.dirty || isSubmitting
                      }
                    >
                      {isSubmitting ? "Saving…" : "Save"}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Form>
          )}
        </Formik>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete relationship type?"
        content="This will permanently remove the selected relationship type."
        onModalConfirmClick={() => {
          if (isSubmitting) return;

          handleDelete();
        }}
        secondaryButtonText="Cancel"
        primaryButtonText={isSubmitting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={doubleClickOnDelete}
      />
    </Box>
  );
};

export default SettingsPropertiesProfileRelationshipTypes;
